import { motion, AnimatePresence } from 'framer-motion';
import useSimpleCartStore from '../store/simpleCartStore';
import { theme } from '../theme';

/**
 * Simple Cart Component
 * No backend required - uses localStorage via Zustand
 */
export default function SimpleCart({ setCurrentPage, showSuccess, showError }) {
  const {
    items,
    getItemCount,
    getTotal,
    removeFromCart,
    updateQuantity,
    clearCart,
  } = useSimpleCartStore();

  const itemCount = getItemCount();
  const total = getTotal();

  const handleRemove = (itemId) => {
    removeFromCart(itemId);
    showSuccess?.('Item removed from cart');
  };

  const handleCheckout = () => {
    showSuccess?.('Checkout coming soon! For now, this is a demo cart.');
  };

  // Empty cart view
  if (itemCount === 0) {
    return (
      <section style={{
        minHeight: '100vh',
        width: '100%',
        padding: '140px clamp(20px, 5vw, 40px) 80px',
        background: theme.colors.bg,
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div style={{
              width: '120px',
              height: '120px',
              margin: '0 auto 32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: theme.colors.surface,
              borderRadius: '50%',
              border: `2px dashed ${theme.colors.border}`,
            }}>
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke={theme.colors.textMuted} strokeWidth="2">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
            </div>

            <h1 style={{
              fontFamily: theme.fonts.display,
              fontSize: '32px',
              fontWeight: '700',
              color: theme.colors.text,
              margin: '0 0 16px 0',
            }}>
              YOUR CART IS EMPTY
            </h1>

            <p style={{
              fontFamily: theme.fonts.body,
              fontSize: '16px',
              color: theme.colors.textMuted,
              margin: '0 0 32px 0',
              lineHeight: '1.6',
            }}>
              Start designing your perfect NFC business card!
            </p>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCurrentPage('design')}
              style={{
                padding: '16px 40px',
                background: theme.colors.gradient,
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontFamily: theme.fonts.display,
                fontSize: '16px',
                fontWeight: '600',
                color: theme.colors.bg,
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              DESIGN YOUR CARD
            </motion.button>
          </motion.div>
        </div>
      </section>
    );
  }

  // Cart with items
  return (
    <section style={{
      minHeight: '100vh',
      width: '100%',
      padding: '140px clamp(20px, 5vw, 40px) 80px',
      background: theme.colors.bg,
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{ marginBottom: '48px' }}
        >
          <h1 style={{
            fontFamily: theme.fonts.display,
            fontSize: 'clamp(32px, 5vw, 48px)',
            fontWeight: '800',
            color: theme.colors.text,
            margin: '0 0 8px 0',
          }}>
            SHOPPING CART
          </h1>
          <p style={{
            fontFamily: theme.fonts.body,
            fontSize: '16px',
            color: theme.colors.textMuted,
            margin: 0,
          }}>
            {itemCount} {itemCount === 1 ? 'item' : 'items'} in your cart
          </p>
        </motion.div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 380px',
          gap: '40px',
          alignItems: 'start',
        }}>
          {/* Cart Items */}
          <div>
            <AnimatePresence mode="popLayout">
              {items.map((item, index) => (
                <CartItem
                  key={item.id}
                  item={item}
                  index={index}
                  onRemove={handleRemove}
                  onUpdateQuantity={updateQuantity}
                  theme={theme}
                />
              ))}
            </AnimatePresence>
          </div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{
              position: window.innerWidth < 768 ? 'relative' : 'sticky',
              top: '140px',
            }}
          >
            <div style={{
              background: theme.colors.surface,
              borderRadius: '16px',
              padding: '32px',
              border: `1px solid ${theme.colors.border}`,
            }}>
              <h3 style={{
                fontFamily: theme.fonts.display,
                fontSize: '20px',
                fontWeight: '700',
                color: theme.colors.text,
                margin: '0 0 24px 0',
              }}>
                ORDER SUMMARY
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '12px',
                }}>
                  <span style={{
                    fontFamily: theme.fonts.body,
                    fontSize: '14px',
                    color: theme.colors.textMuted,
                  }}>
                    Subtotal
                  </span>
                  <span style={{
                    fontFamily: theme.fonts.mono,
                    fontSize: '14px',
                    color: theme.colors.text,
                  }}>
                    ${(total / 100).toFixed(2)}
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '12px',
                }}>
                  <span style={{
                    fontFamily: theme.fonts.body,
                    fontSize: '14px',
                    color: theme.colors.textMuted,
                  }}>
                    Shipping
                  </span>
                  <span style={{
                    fontFamily: theme.fonts.mono,
                    fontSize: '14px',
                    color: theme.colors.text,
                  }}>
                    FREE
                  </span>
                </div>

                <div style={{
                  borderTop: `1px solid ${theme.colors.border}`,
                  paddingTop: '12px',
                  marginTop: '12px',
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}>
                    <span style={{
                      fontFamily: theme.fonts.display,
                      fontSize: '18px',
                      fontWeight: '700',
                      color: theme.colors.text,
                    }}>
                      Total
                    </span>
                    <span style={{
                      fontFamily: theme.fonts.mono,
                      fontSize: '24px',
                      fontWeight: '700',
                      color: theme.colors.accent,
                    }}>
                      ${(total / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCheckout}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: theme.colors.gradient,
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontFamily: theme.fonts.display,
                  fontSize: '16px',
                  fontWeight: '700',
                  color: theme.colors.bg,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: '12px',
                }}
              >
                CHECKOUT (DEMO)
              </motion.button>

              <button
                onClick={() => setCurrentPage('design')}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'transparent',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontFamily: theme.fonts.body,
                  fontSize: '14px',
                  color: theme.colors.text,
                }}
              >
                Continue Shopping
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Cart Item Component
function CartItem({ item, index, onRemove, onUpdateQuantity, theme }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      layout
      style={{
        background: theme.colors.surface,
        borderRadius: '16px',
        padding: '24px',
        border: `1px solid ${theme.colors.border}`,
        marginBottom: '16px',
      }}
    >
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        {/* Preview */}
        <div style={{
          width: '120px',
          height: '80px',
          background: theme.colors.bgAlt,
          borderRadius: '12px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${theme.colors.border}`,
          overflow: 'hidden',
        }}>
          {item.previewUrl ? (
            <img
              src={item.previewUrl}
              alt="Card preview"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={theme.colors.textMuted} strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          )}
        </div>

        {/* Details */}
        <div style={{ flex: 1, minWidth: '200px' }}>
          <h4 style={{
            fontFamily: theme.fonts.display,
            fontSize: '18px',
            fontWeight: '700',
            color: theme.colors.text,
            margin: '0 0 4px 0',
          }}>
            {item.product?.name || 'NFC Business Card'}
          </h4>

          <p style={{
            fontFamily: theme.fonts.body,
            fontSize: '14px',
            color: theme.colors.textMuted,
            margin: '0 0 16px 0',
          }}>
            {item.design?.name || 'Custom Design'}
          </p>

          {/* Quantity Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                aria-label="Decrease quantity"
                disabled={item.quantity <= 1}
                style={{
                  width: '32px',
                  height: '32px',
                  background: theme.colors.bgAlt,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '8px',
                  cursor: item.quantity <= 1 ? 'not-allowed' : 'pointer',
                  fontFamily: theme.fonts.mono,
                  fontSize: '16px',
                  color: theme.colors.text,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: item.quantity <= 1 ? 0.5 : 1,
                }}
              >
                −
              </button>
              <span style={{
                fontFamily: theme.fonts.mono,
                fontSize: '14px',
                color: theme.colors.text,
                minWidth: '24px',
                textAlign: 'center',
              }}
              aria-label={`Quantity: ${item.quantity}`}
              >
                {item.quantity}
              </span>
              <button
                onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                aria-label="Increase quantity"
                style={{
                  width: '32px',
                  height: '32px',
                  background: theme.colors.bgAlt,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: theme.fonts.mono,
                  fontSize: '16px',
                  color: theme.colors.text,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                +
              </button>
            </div>

            <button
              onClick={() => onRemove(item.id)}
              aria-label={`Remove ${item.product?.name || 'item'} from cart`}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                border: `1px solid ${theme.colors.error}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontFamily: theme.fonts.body,
                fontSize: '12px',
                color: theme.colors.error,
              }}
            >
              Remove
            </button>
          </div>
        </div>

        {/* Price */}
        <div style={{ textAlign: 'right', minWidth: '100px' }}>
          <div style={{
            fontFamily: theme.fonts.mono,
            fontSize: '20px',
            fontWeight: '700',
            color: theme.colors.accent,
            marginBottom: '4px',
          }}>
            ${((item.price * item.quantity) / 100).toFixed(2)}
          </div>
          <div style={{
            fontFamily: theme.fonts.mono,
            fontSize: '12px',
            color: theme.colors.textMuted,
          }}>
            ${(item.price / 100).toFixed(2)} each
          </div>
        </div>
      </div>
    </motion.div>
  );
}
